import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithErrorHandling } from "../utils/service";

export const useFetchCollections = (sortCriteria, enabled = true) => {
  const sortParams = new URLSearchParams({
    sortField: sortCriteria?.field || "created_at",
    sortOrder: sortCriteria?.direction || "DESC",
  }).toString();

  return useQuery({
    queryKey: ["collections", sortCriteria],
    queryFn: () => fetchWithErrorHandling(`/v1/collections/list?${sortParams}`),
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled,
  });
};

export const useFetchSimplified = () => {
  return useQuery({
    queryKey: ["collectionsSimplified"],
    queryFn: () => fetchWithErrorHandling(`/v1/collections/simplified`),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const collectionUnlockStorageKey = (uuid) => `collectionUnlock:${uuid}`;

export const useFetchCollection = (uuid) => {
  return useQuery({
    queryKey: ["collectionDetail", uuid],
    queryFn: () => {
      // Session-scoped unlock token for password-protected collections
      const unlockToken = sessionStorage.getItem(
        collectionUnlockStorageKey(uuid)
      );
      return fetchWithErrorHandling(
        `/v1/collections/${uuid}/details`,
        unlockToken ? { headers: { "x-collection-token": unlockToken } } : {}
      );
    },
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: !!uuid,
  });
};

export const useCreateCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collectionData) =>
      fetchWithErrorHandling("/v1/collections/create", {
        method: "POST",
        body: JSON.stringify(collectionData),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });
};

export const useDeleteCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/collections/${uuid}/delete`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });
};

export const useUpdateCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collection) =>
      fetchWithErrorHandling(`/v1/collections/${collection.uuid}/update`, {
        method: "PUT",
        body: JSON.stringify(collection),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });
};

export const useLockCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, password }) =>
      fetchWithErrorHandling(`/v1/collections/${uuid}/lock`, {
        method: "PUT",
        body: JSON.stringify({ password }),
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      queryClient.invalidateQueries({
        queryKey: ["collectionDetail", variables.uuid],
      });
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
    },
  });
};

export const useUnlockCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, password }) =>
      fetchWithErrorHandling(`/v1/collections/${uuid}/unlock`, {
        method: "POST",
        body: JSON.stringify({ password }),
      }),
    onSuccess: (data, variables) => {
      if (data.status === "success" && data.content?.token) {
        sessionStorage.setItem(
          collectionUnlockStorageKey(variables.uuid),
          data.content.token
        );
        queryClient.invalidateQueries({
          queryKey: ["collectionDetail", variables.uuid],
        });
      }
    },
  });
};

export const useRemoveCollectionLock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, password }) =>
      fetchWithErrorHandling(`/v1/collections/${uuid}/lock`, {
        method: "DELETE",
        body: JSON.stringify({ password }),
      }),
    onSuccess: (data, variables) => {
      if (data.status === "success") {
        sessionStorage.removeItem(collectionUnlockStorageKey(variables.uuid));
        queryClient.invalidateQueries({ queryKey: ["collections"] });
        queryClient.invalidateQueries({
          queryKey: ["collectionDetail", variables.uuid],
        });
        queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      }
    },
  });
};

export const useTopCollections = () => {
  return useQuery({
    queryKey: ["collections"],
    queryFn: () => fetchWithErrorHandling(`/v1/collections/top`),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useFetchPublicCollection = (uuid, domain) => {
  return useQuery({
    queryKey: ["publicCollection", uuid, domain],
    queryFn: () => fetchWithErrorHandling(`/v1/collections/public/${uuid}/${domain}`),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!uuid && !!domain,
  });
};

// Public "channel" page: all of one user's public collections (unauthenticated).
export const useFetchUserPublicCollections = (userUuid) => {
  return useQuery({
    queryKey: ["userPublicCollections", userUuid],
    queryFn: () =>
      fetchWithErrorHandling(`/v1/collections/public/user/${userUuid}`),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!userUuid,
  });
};
